import axios from 'axios';
import appState from '../state/AppStateContainer';

export type Result = {
  id: string,
  title: string,
  tags: {
    type: 'Project' | 'User' | 'Supplier',
    suppliers: {
      projectInfo?: {
        name: string,
        id: string,
      },
      areaInfo?: {
        name: string,
        id: string,
      },
      supplierInfo: {
        name: string,
        organizationNumber: string,
        id: string,
      }
    }[],
  }
};

export type MonitoredSupplier = {
  name: string;
  organizationNumber: string;
  monitoredSince: Date;
  lastEventDate: Date;
  projects?: {
    _id: string;
    name: string;
    slug: string;
    area: any[]
  }[];
}

export type MonitoringType = {
  value: string,
  description: string,
  codes: {
    [key: string]: string,
  },
  types: {
    [key: string]: string,
  },
}

export type MonitoringEvent = {
  date: string,
  week: string,
  description: string,
  code: string,
  type: string,
  supplier: {
    notFound?: boolean,
    name: string,
    organizationNumber: string,
  }
}

export type Sort = {
  field?: string;
  order?: 'asc' | 'desc';
}

export type RemoveMonitoringOutcome = {
  /** Fresh post-removal monitored list, or null when the re-list itself failed. */
  suppliers: MonitoredSupplier[] | null;
  removed: number;
  requested: number;
  /** Requested numbers that were already not monitored — the goal state held. */
  skippedOrgNumbers: string[];
  /** Removals the provider refused — still monitored, worth a retry. */
  failed: { organizationNumber: string, name?: string }[];
};

/**
 * The Syna backend answers a bare array on success (its route is all-or-nothing),
 * the Creditsafe backend answers outcome buckets — fold both into one shape.
 */
export function normalizeRemoveMonitoringResult(data: unknown, requestedCount: number): RemoveMonitoringOutcome {
  if (Array.isArray(data)) {
    return { suppliers: data, removed: requestedCount, requested: requestedCount, skippedOrgNumbers: [], failed: [] };
  }
  const result = (data ?? {}) as Partial<RemoveMonitoringOutcome>;
  return {
    suppliers: Array.isArray(result.suppliers) ? result.suppliers : null,
    removed: typeof result.removed === 'number' ? result.removed : 0,
    requested: typeof result.requested === 'number' ? result.requested : requestedCount,
    skippedOrgNumbers: Array.isArray(result.skippedOrgNumbers) ? result.skippedOrgNumbers.map(String) : [],
    failed: Array.isArray(result.failed) ? result.failed : [],
  };
}

function getMonitoringBasePath(): string {
  const org = appState.getOrgFromUrl();
  if (org?.hasSyna) return '/api/supplier-monitoring';
  if (org?.hasCreditsafe) return '/api/creditsafe-monitoring';
  return '/api/supplier-monitoring';
}

class ServiceSupplierMonitoring {
  async search(query: string): Promise<Result[]> {
    const response = await axios.get(`${getMonitoringBasePath()}/search`, { params: { q: query } });
    return response.data;
  }
  async fetchMonitoredSuppliers(sort?: Sort): Promise<MonitoredSupplier[]> {
    const response = await axios.get(`${getMonitoringBasePath()}/monitored-entities`, { params: { sort } });
    return response.data;
  }
  async addMonitoredEntity(data: { projectId?: string, areaId?: string, supplierId?: string }): Promise<MonitoredSupplier[]> {
    const response = await axios.post(`${getMonitoringBasePath()}/monitored-entities`, data);
    return response.data;
  }
  async removeMonitoredSuppliers(organizationNumbers: string[]): Promise<RemoveMonitoringOutcome> {
    const response = await axios.delete(`${getMonitoringBasePath()}/monitored-entities`, { data: { organizationNumbers } });
    return normalizeRemoveMonitoringResult(response.data, new Set(organizationNumbers.map(String)).size);
  }
  async fetchMonitoringTypes(): Promise<MonitoringType[]> {
    const response = await axios.get(`${getMonitoringBasePath()}/monitoring-types`);
    return response.data;
  }
  async fetchFilteredSuppliers(query: string): Promise<Result[]> {
    const response = await axios.get(`${getMonitoringBasePath()}/filter-search`, { params: { q: query } });
    return response.data;
  }
  async fetchEvents(sort?: Sort, filteredEventTypes?: string[], filteredSupplierOrgNumbers?: string[]): Promise<MonitoringEvent[]> {
    const response = await axios.get(`${getMonitoringBasePath()}/events`, { params: { sort, filteredEventTypes, filteredSupplierOrgNumbers } });
    return response.data;
  }
  async fetchMonitoredSupplier(organizationNumber: string): Promise<MonitoredSupplier> {
    const response = await axios.get(`${getMonitoringBasePath()}/monitored-supplier`, { params: { organizationNumber } });
    return response.data;
  }
  async fetchSuppliersFromProject(projectId: string): Promise<Result> {
    const response = await axios.get(`${getMonitoringBasePath()}/suppliers-from-project`, { params: { projectId } });
    return response.data;
  }
}

export default new ServiceSupplierMonitoring();
